import {Component, OnDestroy, OnInit} from '@angular/core';
import {AssetsService} from '../../services/assets.service';
import {MapService} from '../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import {AssetRepresentationsService} from '../../services/asset-representations.service';
import {MapIconsService} from '../../services/map-icons.service';
import {MapLayerControl} from '../../model/custom/map/map-layer-control';
import {MapAsset} from '../../model/custom/map/map-asset';
import {MapAssetRepresentation} from '../../model/custom/map/map-asset-representation';
import {translate} from '@ngneat/transloco';
import {ComponentCanDeactivate} from '../../common/can-deactivate/component-can-deactivate';
import {animate, style, transition, trigger} from '@angular/animations';
import {FeatureCollection, Geometry} from 'geojson';
import {LoggedUser} from '../../common/logged-user.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate(500, style({opacity: 1}))
      ]),
      transition(':leave', [
        animate(500, style({opacity: 0}))
      ])
    ])
  ]
})
export class MapComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  public readonly DEFAULT_ZOOM = 1.5;
  public readonly DEFAULT_LATITUDE = 15;
  public readonly DEFAULT_LONGITUDE = 0;

  public mapAssets: MapAsset[];
  public mapAssetRepresentations: MapAssetRepresentation[];
  public uniqueMapAssetRepresentations: MapAssetRepresentation[] = [];

  public map: mapboxgl.Map;
  public mapData: FeatureCollection<Geometry>;
  public mapDataFiltered: FeatureCollection<Geometry>;
  public representationVisibility: Map<string, boolean>;

  public filtersVisible = false;
  public displayDetails = false;
  public detailsMode: 'asset' | 'cluster';

  public assetDetailsUuid: string;
  public assetDetailsUuids: string[];
  public hashedMapAsset: Map<string, MapAsset>;

  public mapIsLoading: boolean;

  public possibleStatus: string[];

  private loadedRepresentationIconIds: Set<string>;

  private loggedUserSubscription: Subscription;

  private mapCluster: {cluster: boolean, clusterMaxZoom: number, clusterRadius: number};

  constructor(private assetsService: AssetsService,
              public assetRepresentationsService: AssetRepresentationsService,
              public mapIconsService: MapIconsService,
              private mapService: MapService,
              private loggedUser: LoggedUser
  ) {
    this.mapIsLoading = true;
    this.hashedMapAsset = new Map();
    this.possibleStatus = [];
    this.loadedRepresentationIconIds = new Set<string>();
  }

  public ngOnInit(): void {
    this.representationVisibility = new Map<string, boolean>();
    this.possibleStatus = ['default', ...this.assetRepresentationsService.getImageStatusTypes()];

    this.loggedUserSubscription = this.loggedUser.userPreferences.subscribe((userData) => {
      this.assetRepresentationsService.buildAssetRepresentationPreferences(userData.preferences);

      this.mapCluster = userData.preferences.mapbox.config;

      this.loadMapAndAssetData();
    });
  }

  private loadMapAndAssetData(): void {
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, this.DEFAULT_LATITUDE, this.DEFAULT_LONGITUDE, 'globalMap');
    this.loadAssetData();
  }

  private async buildAssetRepresentationWithCustomIconsAndStatus(): Promise<void> {
    const hashMapAssetRepresentation: Map<number, MapAssetRepresentation> = new Map<number, MapAssetRepresentation>();
    let iconRenderingPromises: Promise<MapAssetRepresentation>[] = [];

    this.mapAssetRepresentations.forEach((mapAssetRepresentation) => {
      if (mapAssetRepresentation.id !== undefined) {
        hashMapAssetRepresentation.set(mapAssetRepresentation.id, mapAssetRepresentation);
      }
    });

    for (const asset of this.mapAssets) {
      this.hashedMapAsset.set(asset.uuid, asset);
      this.processAsset(asset, hashMapAssetRepresentation, iconRenderingPromises);
    }

    const iconPromisesResolved: MapAssetRepresentation[] = await Promise.all(iconRenderingPromises);

    iconPromisesResolved.forEach((representationResolved: MapAssetRepresentation) => {
      this.mapAssetRepresentations.push(representationResolved);
    });
  }

  private processAsset(asset: MapAsset, mapAssetRepresentation: Map<number, MapAssetRepresentation>, iconRenderingPromises: Promise<MapAssetRepresentation>[]) {
    const baseRepresentation: MapAssetRepresentation = mapAssetRepresentation.get(asset.representationId);

    if (!baseRepresentation) {
      asset.representationName = translate('empty.representation');
      asset.representationId = undefined;
      asset.hasRepresentationTargetColor = true;
      return;
    }

    asset.representationName = baseRepresentation.name;

    if (!baseRepresentation.icon) {
      asset.representationId = undefined;
      asset.hasRepresentationTargetColor = true;
      return;
    }

    asset.hasRepresentationTargetColor = !!baseRepresentation.targetColor;
    if (!asset.hasRepresentationTargetColor) return;

    const identifier: string = asset.getIdentifier();

    if (!this.mapIconsService.hasIcon(identifier)) {

      if (!asset.status) {
        this.mapIconsService.setIcon(identifier, baseRepresentation);
      }

      for (const status of this.assetRepresentationsService.getImageStatusTypes()) {
        const newRepresentation: MapAssetRepresentation = MapAssetRepresentation.cloneWithStatus(baseRepresentation, status);
        const newAsset: MapAsset = MapAsset.cloneWithStatus(asset, status);
        if (!this.mapIconsService.hasIcon(newAsset.getIdentifier())) {
          iconRenderingPromises.push(this.mapIconsService.queueBuildCustomIconBy(newAsset.getIdentifier(), newRepresentation));
        }
      }
    }
  }

  private renderDefaultIcons(): Promise<MapAssetRepresentation>[] {
    const undefinedIconsPromises: Promise<MapAssetRepresentation>[] = [];
    this.assetRepresentationsService.getImageStatusTypes().forEach((status: string): void => {
      const undefinedAssetRepresentation: MapAssetRepresentation = new MapAssetRepresentation(this.mapIconsService.getDefaultIcon(), undefined, translate(`empty.representation`), this.mapIconsService.getDefaultIconMainColor(), status);
      undefinedIconsPromises.push(this.mapIconsService.buildCustomIconBy(undefinedAssetRepresentation.getIdentifier(), undefinedAssetRepresentation));
    });
    return undefinedIconsPromises;
  }

  private initMapAssetsAndMapAssetRepresentation(values: any[]): void {
    this.mapAssets = [];
    this.mapAssetRepresentations = [];

    values[0].mapAssets.forEach((mapAsset: MapAsset): void => {
      this.mapAssets.push(new MapAsset(mapAsset.lat, mapAsset.lon, mapAsset.uuid, mapAsset.representationId, mapAsset.representationName, mapAsset.status));
    });
    values[1].assetRepresentationIcons.forEach((mapAssetRepresentation: MapAssetRepresentation): void => {
      this.mapAssetRepresentations.push(new MapAssetRepresentation(mapAssetRepresentation.icon, mapAssetRepresentation.id, mapAssetRepresentation.name, mapAssetRepresentation.targetColor, mapAssetRepresentation.status));
    });

    for (let i = 2; i < values.length; i++) {
      this.mapAssetRepresentations.push((values[i] as MapAssetRepresentation));
    }

    this.mapAssetRepresentations.push(new MapAssetRepresentation(this.mapIconsService.getNewDefaultIcon(), undefined, translate('empty.representation')));
  }

  private loadAssetData(): void {
    const undefinedIconsPromises: Promise<MapAssetRepresentation>[] = this.renderDefaultIcons();

    const promises: Promise<any>[] = [
      this.assetsService.getMapAssets().toPromise(),
      this.assetRepresentationsService.getAllAssetRepresentationIcons().toPromise(),
      ...undefinedIconsPromises
    ];

    Promise.all(promises).then(async (values: any[]): Promise<void> => {

      this.initMapAssetsAndMapAssetRepresentation(values);

      await this.buildAssetRepresentationWithCustomIconsAndStatus();

      this.mapAssetRepresentations.sort((a: MapAssetRepresentation, b: MapAssetRepresentation): number => {
        if (a.name < b.name) {
          return -1;
        }
        return (a.name > b.name) ? 1 : 0;
      });

      this.mapAssetRepresentations.forEach(representation => {
        if (this.uniqueMapAssetRepresentations.filter(repr2 => repr2.name === representation.name).length === 0 && representation.status === undefined) {
          this.uniqueMapAssetRepresentations.push(representation);
          this.representationVisibility.set(representation.name, true);
        }
      });

      this.buildMapImagesFromIcons();

    });
  }

  private async buildMapImagesFromIcons(): Promise<void> {
    for (const representation of this.mapAssetRepresentations) {
      if (representation.icon) {
        const iconBase64: string = `data:image/png;base64,${representation.icon}`;
        const imageElement: HTMLImageElement = new Image(45, 45);
        const id: string = representation.getIdentifier();

        imageElement.src = iconBase64;
        imageElement.onload = (): void => {
          this.map.addImage(`image-${id}`, imageElement);
        };
        this.loadedRepresentationIconIds.add(id);
      }
    }
    this.addPoints();
  }

  private getIcon(mapAsset: MapAsset): string {
    const id: string = mapAsset.getIdentifier();
    const loaded: boolean = this.loadedRepresentationIconIds.has(id);
    const imageUndefinedIcon: string = mapAsset.status ? `image-undefined-${mapAsset.status}` : 'image-undefined';
    return loaded ? `image-${id}` : imageUndefinedIcon;
  }

  private addPoints(): void {
    const features: any[] = [];
    for (const mapAsset of this.mapAssets) {
      const icon = this.getIcon(mapAsset);
      features.push({
        type: 'Feature',
        properties: {
          uuid: mapAsset.uuid,
          status: mapAsset.status ?? 'default',
          icon,
          representation: mapAsset.representationName ?? 'default'
        },
        geometry: {
          type: 'Point',
          coordinates: [
            mapAsset.lon,
            mapAsset.lat
          ]
        }
      });
    }

    this.mapData = {
      type: 'FeatureCollection',
      features
    };

    setTimeout(() => this.buildMainMap(), 1000);
  }

  private buildMainMap(): void {
    this.map
      .addSource('data', {
        type: 'geojson',
        data: this.mapData,
        cluster: this.mapCluster.cluster,
        clusterMaxZoom: this.mapCluster.clusterMaxZoom,
        clusterRadius: this.mapCluster.clusterRadius
      })
      .addLayer({
        id: 'add-3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 10,
        paint: {
          'fill-extrusion-color': '#a7d8e4',
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            15,
            15.05,
            ['get', 'height']
          ],
          'fill-extrusion-base': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'min_height']
          ],
          'fill-extrusion-opacity': 0.8
        }
      })
      .addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'data',
        filter: ['has', 'point_count'],
        paint: {
          'circle-opacity': 0.7,
          'circle-blur': 0.6,
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#81D4FA', 100,
            '#42A5F5', 750,
            '#1976D2'
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            30, 100,
            40, 750,
            80
          ]
        }
      })
      .addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'data',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      })
      .addLayer({
        id: 'unclustered-points',
        type: 'symbol',
        source: 'data',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-allow-overlap': true,
          'icon-image': ['get', 'icon']
        }
      })
      .on('click', 'unclustered-points', (e) => {
        const feature = e.features[0];
        if (feature.geometry.type === 'Point') {
          const coordinates = feature.geometry.coordinates.slice();
          const assetUuid = feature.properties.uuid;
          this.showAssetDetails(assetUuid, coordinates);
        }
      })
      .on('click', 'clusters', (e) => {
        const feature = this.map.queryRenderedFeatures(e.point, {layers: ['clusters']})[0];
        if (feature.geometry.type === 'Point') {
          const coordinates = feature.geometry.coordinates.slice();
          const clusterId = feature.properties.cluster_id;
          const pointCount = feature.properties.point_count;
          this.showClusterAssetList(clusterId, pointCount, coordinates);
        }
      })
      .on('click', () => {
        this.filtersVisible = false;
      })
      .addControl(new MapLayerControl(document.getElementById('map-layer-control')));

    setTimeout(() => {
      if (this.mapData.features.length > 0) {
        let bounds = new mapboxgl.LngLatBounds();
        this.mapData.features.forEach((feature) => {
          const coords = feature.geometry['coordinates'];
          if (coords[0] <= 180 && coords[0] >= -180 && coords[1] <= 90 && coords[1] >= -90) {
            bounds.extend(coords);
          }
        });
        this.map.fitBounds(bounds, {padding: 50});
      }
      this.mapIsLoading = false;
    }, 1000);
  }

  public setDisplayDetails(active: boolean): void {
    this.displayDetails = active;
  }

  public showAssetDetails(assetUuid: string, coordinates: any): void {
    if (coordinates) {
      this.map.easeTo({center: coordinates});
    }
    this.detailsMode = 'asset';
    this.assetDetailsUuid = assetUuid;
    this.setDisplayDetails(true);
  }

  private showClusterAssetList(clusterId: number, pointCount: number, coordinates: any): void {
    const source = this.map.getSource('data');
    if (source.type === 'geojson') {
      source.getClusterExpansionZoom(clusterId, (err, newZoom) => {
        if (err) {
          return;
        }
        const currentZoom = this.map.getZoom();
        if (currentZoom >= 19) {
          const clusterSource = this.map.getSource('data');
          if (clusterSource.type === 'geojson') {
            clusterSource.getClusterLeaves(clusterId, pointCount, 0, (error, features) => {
              this.assetDetailsUuids = features.map(feature => feature.properties.uuid);
              this.detailsMode = 'cluster';
              this.setDisplayDetails(true);
            });
          }
        }
        this.map.easeTo({center: coordinates, zoom: newZoom > 19 ? 19 : newZoom});
      });
    }
  }

  public updateAssetStatus(assetUuidChange: string): void {
    const mapData = this.mapDataFiltered ?? this.mapData;
    const feature = mapData.features.find((feature => {
      return feature.properties.uuid === assetUuidChange;
    }));
    const mapAsset: MapAsset = this.hashedMapAsset.get(assetUuidChange);

    feature.properties.icon = this.getIcon(mapAsset);
    feature.properties.status = mapAsset.status;

    const source = this.map.getSource('data');
    if (source.type === 'geojson') {
      source.setData({
        type: 'FeatureCollection',
        features: mapData.features
      });
    }

  }

  public toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
  }

  public canDeactivate(): Promise<boolean> {
    this.displayDetails = false;
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 0);
    });
  }

  public ngOnDestroy(): void {
    this.map?.remove();
    this.loggedUserSubscription.unsubscribe();
    this.mapIconsService.cleanCustomIcons();
  }

}
